import {
  BriefcaseMedical,
  Building2,
  ChevronDown,
  ChevronUp,
  Fingerprint,
  Gauge,
  HandCoins,
  Home,
  LayoutDashboard,
  LucideIcon,
  MessageCircle,
  NotebookText,
  Power,
  ReceiptText,
  Settings,
  Signature,
  User,
  UserRoundPen,
  Users,
  PackageCheck,
  Bell,
  ClipboardList,
} from 'lucide-react';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from '@web/components/ui/sidebar';

import { Link, useNavigate } from '@tanstack/react-router';
import { HRef } from '@web/lib/react-router';

import { useAuthContext } from '@web/auth';
import { useIsMobile } from '@web/hooks/is-mobile';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@web/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@web/components/ui/dropdown-menu';
import { trpc } from '@web/lib/trpc';

import { hasPermission } from '@shared/auth';
import { Route } from '../route';
// NavbarLink component that closes the sidebar on mobile when clicked
function NavbarLink({
  item,
  setOpen,
}: {
  item: { title: string; url: HRef; icon: LucideIcon };
  setOpen: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const handleClick = (e: React.MouseEvent) => {
    // Only take special action on mobile devices
    if (isMobile) {
      // Prevent default Link behavior
      e.preventDefault();

      // Close the sidebar
      setOpen(false);

      // Navigate programmatically after a brief delay to allow sidebar to close
      setTimeout(() => {
        void navigate({ to: item.url });
      }, 100);
    }
    // On desktop, let the Link component handle navigation normally
  };

  return (
    <Link to={item.url}>
      {({ isActive }) => (
        <SidebarMenuItem>
          <SidebarMenuButton isActive={isActive} onClick={handleClick}>
            <item.icon />
            <span>{item.title}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )}
    </Link>
  );
}

// Menu items.

export function Navbar({ open }: { open: boolean }) {
  const { user, signOut } = useAuthContext();
  const isMobile = useIsMobile();
  const { setNavbarOpen } = Route.useRouteContext();

  const { data: profileData } = trpc.user.getUser.useQuery(
    { id: user?.id ?? '' },
    { enabled: !!user?.id },
  );

  if (!user) return null;

  // Menu items.
  const nullableItems: ({
    title: string;
    url: HRef;
    icon: LucideIcon;
  } | null)[] = [
    // {
    //   title: 'Analytics',
    //   url: '/dashboard/analytics',
    //   icon: Gauge,
    // },

    hasPermission(user, 'communication_module', 'access')
      ? {
          title: 'Chat',
          url: '/dashboard/chat',
          icon: MessageCircle,
        }
      : null,

    hasPermission(user, 'contacts_module', 'access')
      ? {
          title: 'Contacts',
          url: '/dashboard/admin/contact/list',
          icon: UserRoundPen,
        }
      : null,

    hasPermission(user, 'doctors_module', 'access')
      ? {
          title: 'Doctors',
          url: '/dashboard/admin/doctors',
          icon: BriefcaseMedical,
        }
      : null,

    hasPermission(user, 'products_module', 'access')
      ? {
          title: 'Products',
          url: '/dashboard/admin/products',
          icon: PackageCheck,
        }
      : null,

    hasPermission(user, 'brands_module', 'access')
      ? {
          title: 'Procedures',
          url: '/dashboard/admin/procedures',
          icon: ClipboardList,
        }
      : null,

    hasPermission(user, 'brands_module', 'access')
      ? {
          title: 'Brands',
          url: '/dashboard/admin/brands',
          icon: Building2,
        }
      : null,
    {
      title: 'Notifications',
      url: '/dashboard/admin/notification',
      icon: Bell,
    },
    {
      title: 'Users',
      url: '/dashboard/admin/users',
      icon: Users,
    },
  ];

  const items = nullableItems.filter((item) => item !== null);

  return (
    <SidebarProvider name="left" className="relative w-min" open={open}>
      <Sidebar variant="floating" collapsible="icon" side="left">
        <SidebarContent>
          <Collapsible defaultOpen className="group/collapsible">
            <SidebarGroup>
              <SidebarGroupLabel asChild>
                <CollapsibleTrigger>
                  Dashboard
                  <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {items.map((item) => (
                      <NavbarLink
                        key={item.title}
                        item={item}
                        setOpen={setNavbarOpen}
                      />
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton>
                    <Settings /> Settings
                    <ChevronUp className="ml-auto" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="top"
                  className="w-[--radix-popper-anchor-width]"
                >
                  <DropdownMenuItem>
                    Interface
                    <DropdownMenuShortcut>
                      <LayoutDashboard />
                    </DropdownMenuShortcut>
                  </DropdownMenuItem>

                  <DropdownMenuItem>
                    Access
                    <DropdownMenuShortcut>
                      <Fingerprint />
                    </DropdownMenuShortcut>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  <DropdownMenuItem
                    className="font-semibold"
                    onClick={() => {
                      // Close sidebar on mobile if signing out
                      if (isMobile) {
                        setNavbarOpen(false);
                      }
                      void signOut();
                    }}
                  >
                    Log Out
                    <DropdownMenuShortcut className="opacity-100">
                      <Power className="stroke-destructive stroke-2" />
                    </DropdownMenuShortcut>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
    </SidebarProvider>
  );
}
